<template>
  <div class="dashboard">
    <!-- Stats Cards -->
    <n-grid :cols="4" :x-gap="16" :y-gap="16">
      <n-gi>
        <n-card>
          <n-statistic label="Total Licenses" :value="stats.total_licenses" />
        </n-card>
      </n-gi>
      <n-gi>
        <n-card>
          <n-statistic label="Active Licenses" :value="stats.active_licenses">
            <template #suffix>
              <n-tag type="success" size="small">Active</n-tag>
            </template>
          </n-statistic>
        </n-card>
      </n-gi>
      <n-gi>
        <n-card>
          <n-statistic label="Expiring in 7 Days" :value="stats.expiring_in_7_days">
            <template #suffix>
              <n-tag v-if="stats.expiring_in_7_days > 0" type="warning" size="small">Alert</n-tag>
            </template>
          </n-statistic>
        </n-card>
      </n-gi>
      <n-gi>
        <n-card>
          <n-statistic label="Expired Licenses" :value="stats.expired_licenses">
            <template #suffix>
              <n-tag v-if="stats.expired_licenses > 0" type="error" size="small">Expired</n-tag>
            </template>
          </n-statistic>
        </n-card>
      </n-gi>
    </n-grid>

    <!-- Revenue Stats -->
    <n-grid :cols="2" :x-gap="16" :y-gap="16" style="margin-top: 16px">
      <n-gi>
        <n-card title="Revenue">
          <n-statistic label="Total Revenue" :value="formatCurrency(stats.total_revenue)" />
          <n-statistic label="This Month" :value="formatCurrency(stats.revenue_this_month)" style="margin-top: 16px" />
        </n-card>
      </n-gi>
      <n-gi>
        <n-card title="Licenses by Module">
          <div v-for="(count, module) in stats.by_module" :key="module" class="module-stat">
            <span class="module-name">{{ module }}</span>
            <n-tag>{{ count }}</n-tag>
          </div>
          <n-empty v-if="Object.keys(stats.by_module || {}).length === 0" description="No licenses yet" />
        </n-card>
      </n-gi>
    </n-grid>

    <!-- Quick Actions -->
    <n-card title="Quick Actions" style="margin-top: 16px">
      <n-space>
        <n-button type="primary" @click="$router.push('/licenses')">
          View All Licenses
        </n-button>
        <n-button @click="$router.push('/businesses')">
          View Businesses
        </n-button>
        <n-button @click="$router.push('/payments')">
          View Payments
        </n-button>
      </n-space>
    </n-card>

    <!-- Recent Licenses -->
    <n-card title="Licenses Expiring Soon" style="margin-top: 16px">
      <n-data-table
        :columns="licenseColumns"
        :data="expiringLicenses"
        :loading="loading"
        :pagination="false"
      />
    </n-card>
  </div>
</template>

<script setup>
import { ref, onMounted, h } from 'vue'
import {
  NCard,
  NGrid,
  NGi,
  NStatistic,
  NTag,
  NButton,
  NSpace,
  NDataTable,
  NEmpty,
} from 'naive-ui'
import { useAdmin } from '../composables/useAdmin'
import dayjs from 'dayjs'

const { getStats, listLicenses, loading } = useAdmin()

const stats = ref({
  total_licenses: 0,
  active_licenses: 0,
  expired_licenses: 0,
  trial_licenses: 0,
  expiring_in_7_days: 0,
  expiring_in_30_days: 0,
  total_revenue: 0,
  revenue_this_month: 0,
  by_module: {},
})

const expiringLicenses = ref([])

const licenseColumns = [
  { title: 'Business', key: 'business_name' },
  { title: 'Module', key: 'module_name' },
  {
    title: 'Expiry Date',
    key: 'expiry_date',
    render: (row) => dayjs(row.expiry_date).format('MMM DD, YYYY'),
  },
  {
    title: 'Days Left',
    key: 'days_remaining',
    render: (row) => {
      if (row.is_expired) {
        return h(NTag, { type: 'error', size: 'small' }, () => 'Expired')
      }
      if (row.days_remaining <= 7) {
        return h(NTag, { type: 'warning', size: 'small' }, () => `${row.days_remaining} days`)
      }
      return h(NTag, { type: 'success', size: 'small' }, () => `${row.days_remaining} days`)
    },
  },
  {
    title: 'Status',
    key: 'status',
    render: (row) => {
      const types = { active: 'success', expired: 'error', trial: 'info', suspended: 'warning' }
      return h(NTag, { type: types[row.status] || 'default', size: 'small' }, () => row.status)
    },
  },
]

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
  }).format(value || 0)
}

onMounted(async () => {
  try {
    stats.value = await getStats()

    // Get expiring licenses
    const response = await listLicenses({ expiring: true, limit: 10 })
    expiringLicenses.value = response.licenses || []
  } catch (error) {
    console.error('Failed to load dashboard:', error)
  }
})
</script>

<style scoped>
.dashboard {
  max-width: 1200px;
}

.module-stat {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.module-stat:last-child {
  border-bottom: none;
}

.module-name {
  text-transform: capitalize;
}
</style>
